/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react'

import authorization from 'live/services/authorization'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { IAlarmsAsset } from 'plugin-codaf-risk-ext-bowtie/BowtieAlarms/BowtieAlarms'
import {
    createAlarmSettings,
    getAlarmSettings,
    listBowties,
    updateAlarmSettings
} from 'plugin-codaf-risk-ext-bowtie/services'
import { listAlarmGroups } from 'plugin-codaf-risk-ext-bowtie/services/alarms'
import { IBowtie } from 'plugin-codaf-risk-ext-bowtie/types'
import { IAlarmBowtie, IAlarmGroup, IAlarmSettings } from 'plugin-codaf-risk-ext-bowtie/types/alarms'

import { AlarmSettingsTitle, AlarmSettingsCards } from './components'
import { NewGroupSubscriptions } from './components/NewGroupSubscriptions/NewGroupSubscriptions'
import { AlarmSettings, BowtieAlarmsContainer, NoInfo } from './styles'

export const BowtieAlarmsNotificationSettings: React.FC = () => {
    const [isEditingGroup, setIsEditingGroup] = useState<boolean>(false)
    const [assetsList, setAssetsList] = useState<IAlarmsAsset[]>([])
    const [inLoading, setInLoading] = useState(true)
    const [bowtieLoading, setBowtieLoading] = useState(true)
    const [currentUserAssetList, setCurrentUserAssetList] = useState<IAlarmsAsset[]>()
    const [currentUserAlarmSettings, setCurrentUserAlarmSettings] = useState<IAlarmSettings>()
    const [currentUserInfo, setCurrentUserInfo] = useState<IAlarmSettings>()

    const [selectedBowtie, setSelectedBowtie] = useState<Map<number, Map<number, number[]>>>(
        new Map(assetsList.map(asset => [asset.id, new Map(asset.operations.map(operation => [operation.id, []]))]))
    )
    const currentUser: string = authorization.user.username

    const playSound = (): void => {
        new Audio('/content/plugin-codaf-risk-ext-bowtie/WarningAlarm.mp3').play()
    }

    const handleSave = async (): Promise<void> => {
        try {
            setInLoading(true)

            if (currentUserInfo?.id) {
                await updateAlarmSettings({ ...currentUserAlarmSettings, id: currentUserInfo?.id })
                globalNotification.success(i18n('Alarms notifications settings updated succesfully.'))
            } else {
                await createAlarmSettings(currentUserAlarmSettings)
                globalNotification.success(i18n('Alarms notifications settings created succesfully.'))
            }
        } catch (error) {
            globalNotification.error(
                error.message ?? i18n('An error occurred on saving alarms notifications settings.')
            )
        } finally {
            setInLoading(false)
        }
    }

    const transformFEBowtieFromAPI = (bowties: IBowtie[]): IAlarmsAsset[] => {
        const assets: IAlarmsAsset[] = []

        for (const bowtie of bowties) {
            let assetIndex = assets.findIndex(ast => ast.id === bowtie.asset.id)

            if (assetIndex === -1) {
                assets.push({ ...bowtie.asset, operations: [] })
                assetIndex = assets.length - 1
            }

            let operationIndex = assets[assetIndex].operations.findIndex(
                op => op.id === bowtie.topEvent.hazard.operation.id
            )

            if (operationIndex === -1) {
                assets[assetIndex].operations.push({ ...bowtie.topEvent.hazard.operation, bowties: [] })
                operationIndex = assets[assetIndex].operations.length - 1
            }

            assets[assetIndex].operations[operationIndex].bowties.push(bowtie)
        }

        for (const [idx, asset] of assets.entries()) {
            if (asset.operations.length < 1 || !asset.operations.every(op => op.bowties?.length)) {
                delete assets[idx]
            }
        }

        assets.sort((a, b) => a.name.localeCompare(b.name))

        return assets
    }

    async function getData(): Promise<void> {
        try {
            setBowtieLoading(true)
            const userInfo = await getAlarmSettings(currentUser)
            setCurrentUserInfo(userInfo)
            setCurrentUserAlarmSettings(userInfo)
            const { data: bowtieList } = await listBowties()
            const { data: alarmGroups } = await listAlarmGroups()

            const userGroups = new Set<IAlarmGroup>()
            const bowtieIds = new Set<number>()

            alarmGroups?.map(group =>
                group.alarmUsers.map(user => {
                    if (user.liveUserName === currentUser) {
                        userGroups.add(group)
                    }
                })
            )

            userGroups?.forEach(group =>
                group.alarmBowties.map(ab => {
                    bowtieIds.add(ab.bowtieId)
                })
            )

            const filteredBowties = bowtieList.filter(bowtie => bowtieIds.has(bowtie.id))

            if (userInfo === undefined) {
                setCurrentUserAlarmSettings({
                    ...currentUserAlarmSettings,
                    liveUserName: currentUser,
                    alarmGroups: Array.from(userGroups)
                })
            }

            setAssetsList(transformFEBowtieFromAPI(bowtieList))
            setCurrentUserAssetList(transformFEBowtieFromAPI(filteredBowties))
        } catch (err) {
            console.error(err)
            globalNotification.error(err.message ?? i18n('An error occurred on list bowties.'))
        } finally {
            setBowtieLoading(false)
        }
    }

    const transformAlarmBowties = (selectedBowtie: Map<number, Map<number, number[]>>): void => {
        const bowtieIds: number[] = []
        Array.from(selectedBowtie).map(resource =>
            Array.from(resource[1]).map(operation => operation[1].map(bowtieId => bowtieIds.push(bowtieId)))
        )
        const bowtieTransformedIds: IAlarmBowtie[] = []
        bowtieIds.map(bowtieId => bowtieTransformedIds.push({ bowtieId: bowtieId }))
        setCurrentUserAlarmSettings({ ...currentUserAlarmSettings, selectedBowties: bowtieTransformedIds })
    }

    const handleSubmit = (): void => {
        return null
    }

    useEffect(() => {
        getData()
    }, [])

    useEffect(() => {
        transformAlarmBowties(selectedBowtie)
    }, [selectedBowtie])

    useEffect(() => {
        const handleGetSelectedBowties = async (): Promise<void> => {
            try {
                setInLoading(true)

                if (currentUserInfo?.id) {
                    const alarmSettings = await getAlarmSettings(currentUser)
                    const selectedBts = alarmSettings.selectedBowties

                    setSelectedBowtie(
                        new Map(
                            assetsList.map(asset => [
                                asset.id,
                                new Map(
                                    asset.operations.map(operation => [
                                        operation.id,
                                        operation.bowties
                                            .map(bowtie => {
                                                if (selectedBts.find(ab => ab.bowtieId === bowtie.id)) {
                                                    return bowtie.id
                                                }
                                            })
                                            .filter(e => e)
                                    ])
                                )
                            ])
                        )
                    )
                }
            } catch (error) {
                globalNotification.error(error.message ?? i18n('An error occurred on fetching alarm group.'))
            } finally {
                setInLoading(false)
            }
        }

        handleGetSelectedBowties()
    }, [assetsList])

    return (
        <AlarmSettings>
            <BowtieAlarmsContainer style={isEditingGroup ? { padding: 0 } : {}}>
                <AlarmSettingsTitle handleSave={handleSave} />
                <div className={'cards-settings'}>
                    <AlarmSettingsCards
                        title={'Email Notifications'}
                        text={
                            'If you choose to receive email notifications, all the items you subscribed will be send by email in addition to system notifications.'
                        }
                        labelSwitch={'Receive email notifications'}
                        nameIconButton={'fas fa-file-alt'}
                        onSubmit={handleSubmit}
                        onSwitchChange={e => {
                            setCurrentUserAlarmSettings({ ...currentUserAlarmSettings, emailNotification: e })
                        }}
                        switchValue={currentUserAlarmSettings?.emailNotification}
                    ></AlarmSettingsCards>

                    <AlarmSettingsCards
                        title={'Sounds Notifications'}
                        text={
                            'If you choose to receive sound notifications, all the items you subscribed will be send by sound in addition to system notifications.'
                        }
                        labelSwitch={'Receive sounds notifications'}
                        nameIconButton={'fas fa-file-alt'}
                        textButton={'Send Test sound'}
                        onSubmit={() => playSound()}
                        onSwitchChange={e => {
                            setCurrentUserAlarmSettings({ ...currentUserAlarmSettings, soundNotification: e })
                        }}
                        switchValue={currentUserAlarmSettings?.soundNotification}
                    >
                        {/* <SelectSeverity /> */}
                    </AlarmSettingsCards>
                </div>
                {bowtieLoading ? (
                    <NoInfo>
                        <i className="fa fa-spinner fa-spin" />
                    </NoInfo>
                ) : currentUserAssetList !== undefined && currentUserAssetList.length > 0 ? (
                    <NewGroupSubscriptions
                        assets={currentUserAssetList}
                        setSelectedBowties={setSelectedBowtie}
                        selectedBowties={selectedBowtie}
                    />
                ) : (
                    <div className="no-bowties-assigned">
                        {i18n(
                            `You have no bowties assigned to you. Even though you set your notifications, you won't receive any until the Alarms Admin assign you to a group with bowties associated.`
                        )}
                    </div>
                )}
            </BowtieAlarmsContainer>
        </AlarmSettings>
    )
}
